/**
 * Établissements et contacts d'un tiers (CDC §6.2).
 *
 * Deux blocs, parce que ce sont deux questions distinctes : « où facture-t-on ? »
 * et « à qui parle-t-on ? ». Les mélanger obligerait à créer un établissement
 * pour enregistrer un numéro de téléphone.
 *
 * Les **usages** d'un canal sont montrés en toutes lettres et non cachés dans un
 * réglage : c'est eux qui décideront demain qui reçoit la facture et qui reçoit
 * le rapport d'intervention. Un canal sans usage est consultable mais ne sera
 * jamais choisi comme destinataire — l'écran le dit, plutôt que de laisser
 * croire à un envoi.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  api,
  type Canal,
  type Contact,
  type Etablissement,
  type TypeCanal,
  type UsageCanal,
} from '../api/client';

const USAGES: readonly { code: UsageCanal; libelle: string }[] = [
  { code: 'GENERAL', libelle: 'Général' },
  { code: 'QUOTE', libelle: 'Devis' },
  { code: 'ORDER', libelle: 'Commandes' },
  { code: 'INVOICE', libelle: 'Factures' },
  { code: 'INTERVENTION', libelle: 'Interventions' },
  { code: 'ALERT', libelle: 'Alertes' },
];

const TYPES: readonly { code: TypeCanal; libelle: string }[] = [
  { code: 'EMAIL', libelle: 'Courriel' },
  { code: 'PHONE', libelle: 'Téléphone' },
  { code: 'MOBILE', libelle: 'Mobile' },
];

export function ContactsTiers({ partyId }: { partyId: string }) {
  const [etablissements, setEtablissements] = useState<Etablissement[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [ajoutEtab, setAjoutEtab] = useState(false);
  const [ajoutContact, setAjoutContact] = useState(false);

  const charger = useCallback(async () => {
    try {
      // Les deux listes ensemble : la seconde a besoin de la première pour
      // proposer un établissement de rattachement.
      const [e, c] = await Promise.all([api.establishments(partyId), api.contacts(partyId)]);
      setEtablissements(e);
      setContacts(c);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Contacts illisibles.');
    } finally {
      setChargement(false);
    }
  }, [partyId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const archiverEtab = async (etab: Etablissement) => {
    if (
      !window.confirm(
        `Archiver « ${etab.label} » ?\n\n` +
          'Les contacts rattachés restent : ils continueront d’afficher ce nom, ' +
          'mais l’établissement ne sera plus proposé au rattachement.',
      )
    ) {
      return;
    }
    try {
      await api.archiveEstablishment(etab.id);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Archivage impossible.');
    }
  };

  const archiverContact = async (contact: Contact) => {
    if (!window.confirm(`Archiver « ${nomComplet(contact)} » ?`)) return;
    try {
      await api.archiveContact(contact.id);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Archivage impossible.');
    }
  };

  return (
    <>
      {erreur && <div className="message erreur">{erreur}</div>}

      <div className="carte">
        <div className="entete-carte">
          <h2>Établissements</h2>
          <button className="secondaire discret" onClick={() => setAjoutEtab((v) => !v)}>
            {ajoutEtab ? 'Annuler' : 'Ajouter un établissement'}
          </button>
        </div>

        {ajoutEtab && (
          <FormulaireEtablissement
            partyId={partyId}
            onEnregistre={() => {
              setAjoutEtab(false);
              void charger();
            }}
            onErreur={setErreur}
          />
        )}

        {chargement ? (
          <div className="vide">Chargement…</div>
        ) : etablissements.length === 0 ? (
          <div className="vide">
            Aucun établissement. Un client national se fait souvent facturer agence par agence,
            avec un SIRET distinct par site.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Établissement</th>
                <th style={{ width: 160 }}>SIRET</th>
                <th>Adresse</th>
                <th style={{ width: 110 }}>Contacts</th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {etablissements.map((e) => (
                <tr key={e.id}>
                  <td>
                    <strong>{e.label}</strong>
                    {e.isBillingEntity && <span className="etat actif"> facturation </span>}
                  </td>
                  <td>{e.legalIdentifier ?? <span className="aide">—</span>}</td>
                  <td>{adresse(e) || <span className="aide">—</span>}</td>
                  <td>{e.nombreContacts}</td>
                  <td>
                    <button
                      className="secondaire discret danger-discret"
                      onClick={() => void archiverEtab(e)}
                    >
                      Archiver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="carte">
        <div className="entete-carte">
          <h2>Contacts</h2>
          <button className="secondaire discret" onClick={() => setAjoutContact((v) => !v)}>
            {ajoutContact ? 'Annuler' : 'Ajouter un contact'}
          </button>
        </div>

        {ajoutContact && (
          <FormulaireContact
            partyId={partyId}
            etablissements={etablissements}
            onEnregistre={() => {
              setAjoutContact(false);
              void charger();
            }}
            onErreur={setErreur}
          />
        )}

        {chargement ? (
          <div className="vide">Chargement…</div>
        ) : contacts.length === 0 ? (
          <div className="vide">
            Aucun contact. Un contact n’est pas un utilisateur du logiciel : c’est l’interlocuteur
            chez le client, celui qu’on appelle avant de se déplacer.
          </div>
        ) : (
          <div className="carte-corps">
            {contacts.map((c) => (
              <BlocContact
                key={c.id}
                contact={c}
                onChange={(maj) =>
                  setContacts((liste) => liste.map((x) => (x.id === maj.id ? maj : x)))
                }
                onArchiver={() => void archiverContact(c)}
                onErreur={setErreur}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function BlocContact({
  contact,
  onChange,
  onArchiver,
  onErreur,
}: {
  contact: Contact;
  onChange: (c: Contact) => void;
  onArchiver: () => void;
  onErreur: (m: string | null) => void;
}) {
  const [ajoutCanal, setAjoutCanal] = useState(false);

  const retirerCanal = async (canal: Canal) => {
    if (!window.confirm(`Retirer « ${canal.value} » ?`)) return;
    try {
      onChange(await api.archiveChannel(canal.id));
    } catch (e) {
      onErreur(e instanceof ApiError ? e.message : 'Retrait impossible.');
    }
  };

  const basculerConsentement = async (canal: Canal) => {
    try {
      onChange(await api.updateChannel(canal.id, { consentement: !canal.consentementActif }));
    } catch (e) {
      onErreur(e instanceof ApiError ? e.message : 'Modification impossible.');
    }
  };

  return (
    <div className="contact">
      <div className="contact-entete">
        <div>
          <strong>{nomComplet(contact)}</strong>
          {contact.isPrimary && <span className="etat actif"> principal </span>}
          <div className="aide">
            {contact.jobTitle ?? 'Fonction non précisée'}
            {contact.establishmentLabel && <> · {contact.establishmentLabel}</>}
          </div>
        </div>
        <div className="actions">
          <button className="secondaire discret" onClick={() => setAjoutCanal((v) => !v)}>
            {ajoutCanal ? 'Annuler' : 'Ajouter un moyen'}
          </button>
          <button className="secondaire discret danger-discret" onClick={onArchiver}>
            Archiver
          </button>
        </div>
      </div>

      {ajoutCanal && (
        <FormulaireCanal
          contactId={contact.id}
          onEnregistre={(maj) => {
            setAjoutCanal(false);
            onChange(maj);
          }}
          onErreur={onErreur}
        />
      )}

      {contact.channels.length === 0 ? (
        <div className="aide">Aucun moyen de le joindre.</div>
      ) : (
        <ul className="liste-canaux">
          {contact.channels.map((canal) => (
            <li key={canal.id}>
              <span className="type-canal">
                {TYPES.find((t) => t.code === canal.type)?.libelle ?? canal.type}
              </span>
              <span className="valeur-canal">{canal.value}</span>

              {canal.usages.length === 0 ? (
                <span className="aide">jamais destinataire</span>
              ) : (
                <span className="usages">
                  {canal.usages
                    .map((u) => USAGES.find((x) => x.code === u)?.libelle ?? u)
                    .join(', ')}
                </span>
              )}

              <button
                className={`secondaire discret ${canal.consentementActif ? '' : 'danger-discret'}`}
                title={
                  canal.consentGivenAt
                    ? `Consentement donné le ${jour(canal.consentGivenAt)}` +
                      (canal.consentWithdrawnAt
                        ? `, retiré le ${jour(canal.consentWithdrawnAt)}`
                        : '')
                    : 'Aucun consentement enregistré'
                }
                onClick={() => void basculerConsentement(canal)}
              >
                {canal.consentementActif ? 'consentement donné' : 'sans consentement'}
              </button>

              <button
                className="secondaire discret danger-discret"
                onClick={() => void retirerCanal(canal)}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FormulaireEtablissement({
  partyId,
  onEnregistre,
  onErreur,
}: {
  partyId: string;
  onEnregistre: () => void;
  onErreur: (m: string | null) => void;
}) {
  const [champs, setChamps] = useState({
    label: '',
    legalIdentifier: '',
    addressLine1: '',
    postalCode: '',
    city: '',
  });
  const [facturation, setFacturation] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const maj = (cle: keyof typeof champs) => (e: { target: { value: string } }) =>
    setChamps((c) => ({ ...c, [cle]: e.target.value }));

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (champs.label.trim() === '') return;

    setEnCours(true);
    onErreur(null);
    try {
      await api.createEstablishment(partyId, {
        label: champs.label.trim(),
        ...(champs.legalIdentifier.trim() ? { legalIdentifier: champs.legalIdentifier.trim() } : {}),
        ...(champs.addressLine1.trim() ? { addressLine1: champs.addressLine1.trim() } : {}),
        ...(champs.postalCode.trim() ? { postalCode: champs.postalCode.trim() } : {}),
        ...(champs.city.trim() ? { city: champs.city.trim() } : {}),
        isBillingEntity: facturation,
      });
      onEnregistre();
    } catch (err) {
      onErreur(err instanceof ApiError ? err.message : 'Création impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <form className="saisie-ligne" onSubmit={(e) => void soumettre(e)}>
      <input
        type="text"
        placeholder="Libellé : « Agence de Lyon » *"
        value={champs.label}
        onChange={maj('label')}
        autoFocus
        aria-label="Libellé de l’établissement"
      />
      <input
        type="text"
        placeholder="SIRET"
        value={champs.legalIdentifier}
        onChange={maj('legalIdentifier')}
        aria-label="SIRET"
      />
      <input
        type="text"
        placeholder="Adresse"
        value={champs.addressLine1}
        onChange={maj('addressLine1')}
        aria-label="Adresse"
      />
      <input
        type="text"
        placeholder="Code postal"
        value={champs.postalCode}
        onChange={maj('postalCode')}
        aria-label="Code postal"
      />
      <input
        type="text"
        placeholder="Ville"
        value={champs.city}
        onChange={maj('city')}
        aria-label="Ville"
      />
      <label className="case-monotone">
        <input
          type="checkbox"
          checked={facturation}
          onChange={(e) => setFacturation(e.target.checked)}
        />
        <span>facturation</span>
      </label>
      <button disabled={enCours || champs.label.trim() === ''}>
        {enCours ? 'Création…' : 'Créer'}
      </button>
    </form>
  );
}

function FormulaireContact({
  partyId,
  etablissements,
  onEnregistre,
  onErreur,
}: {
  partyId: string;
  etablissements: readonly Etablissement[];
  onEnregistre: () => void;
  onErreur: (m: string | null) => void;
}) {
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [establishmentId, setEstablishmentId] = useState('');
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (lastName.trim() === '') return;

    setEnCours(true);
    onErreur(null);
    try {
      await api.createContact(partyId, {
        lastName: lastName.trim(),
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(jobTitle.trim() ? { jobTitle: jobTitle.trim() } : {}),
        ...(establishmentId ? { establishmentId } : {}),
      });
      onEnregistre();
    } catch (err) {
      onErreur(err instanceof ApiError ? err.message : 'Création impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <form className="saisie-ligne" onSubmit={(e) => void soumettre(e)}>
      <input
        type="text"
        placeholder="Nom *"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        autoFocus
        aria-label="Nom de famille"
      />
      <input
        type="text"
        placeholder="Prénom"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        aria-label="Prénom"
      />
      <input
        type="text"
        placeholder="Fonction : « Responsable maintenance »"
        value={jobTitle}
        onChange={(e) => setJobTitle(e.target.value)}
        aria-label="Fonction"
      />
      <select
        value={establishmentId}
        onChange={(e) => setEstablishmentId(e.target.value)}
        aria-label="Établissement de rattachement"
      >
        <option value="">Sans établissement</option>
        {etablissements.map((e) => (
          <option key={e.id} value={e.id}>
            {e.label}
          </option>
        ))}
      </select>
      <button disabled={enCours || lastName.trim() === ''}>
        {enCours ? 'Création…' : 'Créer'}
      </button>
    </form>
  );
}

function FormulaireCanal({
  contactId,
  onEnregistre,
  onErreur,
}: {
  contactId: string;
  onEnregistre: (c: Contact) => void;
  onErreur: (m: string | null) => void;
}) {
  const [type, setType] = useState<TypeCanal>('EMAIL');
  const [valeur, setValeur] = useState('');
  const [usages, setUsages] = useState<UsageCanal[]>([]);
  const [consentement, setConsentement] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const basculer = (u: UsageCanal) =>
    setUsages((liste) => (liste.includes(u) ? liste.filter((x) => x !== u) : [...liste, u]));

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (valeur.trim() === '') return;

    setEnCours(true);
    onErreur(null);
    try {
      onEnregistre(
        await api.addChannel(contactId, {
          type,
          value: valeur.trim(),
          usages,
          consentement,
        }),
      );
      setValeur('');
      setUsages([]);
      setConsentement(false);
    } catch (err) {
      onErreur(err instanceof ApiError ? err.message : 'Ajout impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <form className="saisie-canal" onSubmit={(e) => void soumettre(e)}>
      <div className="saisie-ligne">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as TypeCanal)}
          aria-label="Type de moyen"
        >
          {TYPES.map((t) => (
            <option key={t.code} value={t.code}>
              {t.libelle}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder={type === 'EMAIL' ? 'adresse@client.fr' : '06 12 34 56 78'}
          value={valeur}
          onChange={(e) => setValeur(e.target.value)}
          aria-label="Valeur du moyen de contact"
        />
        <label className="case-monotone">
          <input
            type="checkbox"
            checked={consentement}
            onChange={(e) => setConsentement(e.target.checked)}
          />
          <span>consentement donné</span>
        </label>
        <button disabled={enCours || valeur.trim() === ''}>
          {enCours ? 'Ajout…' : 'Ajouter'}
        </button>
      </div>

      <div className="choix-usages">
        <span className="aide">Destinataire pour :</span>
        {USAGES.map((u) => (
          <label key={u.code} className="case-usage">
            <input
              type="checkbox"
              checked={usages.includes(u.code)}
              onChange={() => basculer(u.code)}
            />
            <span>{u.libelle}</span>
          </label>
        ))}
      </div>
    </form>
  );
}

function nomComplet(c: Contact): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ');
}

function adresse(e: Etablissement): string {
  return [e.addressLine1, [e.postalCode, e.city].filter(Boolean).join(' ')]
    .filter((x) => x && x.trim() !== '')
    .join(', ');
}

function jour(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR');
}
